// src/ExcelExporter.jsx
import React from 'react';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

// — MERGE any vertical runs of identical values in a single column —
function mergeColumn(ws, col, startRow, endRow) {
  if (!startRow || !endRow || endRow < startRow) return;
  let mergeStart = startRow;
  let lastVal    = ws.getCell(startRow, col).value;
  for (let r = startRow + 1; r <= endRow + 1; r++) {
    const val = ws.getCell(r, col).value;
    if (val !== lastVal) {
      if (r - 1 > mergeStart) {
        ws.mergeCells(mergeStart, col, r - 1, col);
        ws.getCell(mergeStart, col).alignment = { horizontal: 'center', vertical: 'middle' };
      }
      mergeStart = r;
      lastVal    = val;
    }
  }
}

// ── header styling helpers (same as before) ──
const BLACK = { argb: 'FF000000' };
const GREY  = { argb: 'FFBFBFBF' };

function fillRange(ws, r1, c1, r2, c2, fgColor = GREY) {
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      ws.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor };
    }
  }
}
function borderBottom(ws, r, c1, c2, style = 'medium', color = BLACK) {
  for (let c = c1; c <= c2; c++) {
    const cell = ws.getCell(r, c);
    cell.border = { ...cell.border, bottom: { style, color } };
  }
}
function verticalDivider(ws, col, r1, r2, style = 'medium', color = BLACK) {
  for (let r = r1; r <= r2; r++) {
    const left  = ws.getCell(r, col);
    const right = ws.getCell(r, col + 1);
    left.border  = { ...left.border,  right: { style, color } };
    right.border = { ...right.border, left:  { style, color } };
  }
}
function edgeLine(ws, col, r1, r2, side = 'left', style = 'medium', color = BLACK) {
  for (let r = r1; r <= r2; r++) {
    const cell = ws.getCell(r, col);
    cell.border = { ...cell.border, [side]: { style, color } };
  }
}

export default function ExcelExporter(props) {
  // Accept BOTH patterns: {data} OR {header, rows, sectionRows}
  const data = props.data ?? { header: props.header, rows: props.rows, sectionRows: props.sectionRows };

  const header      = data?.header ?? {};
  const rows        = Array.isArray(data?.rows) ? data.rows : [];
  const sectionRows = Array.isArray(data?.sectionRows) ? data.sectionRows : [];

  const download = async () => {
    if (rows.length === 0) {
      alert('No data to export.');
      return;
    }

    const wb = new ExcelJS.Workbook();

    // Common styles
    const headerFont = { name: 'Calibri', size: 11, bold: true };
    const dataFont   = { name: 'Calibri', size: 11 };
    const centerAll  = { horizontal: 'center', vertical: 'middle' };

    // Fixed column titles (A..Q)
    const titles = [
      'Pos Nr. (Côté 1)', 'DPG (Côté 1)', 'Matériel (Côté 1)',
      'Kam (Côté 1)',     'Cosse (Côté 1)', 'Tulle (Côté 1)',
      'Câble',            'Matériel',        'section',
      'Longueur (mm)',    'Couleur 1',       'Couleur 2',
      'Pos Nr. (Côté 2)', 'Matériel (Côté 2)', 'Kam (Côté 2)',
      'Cosse (Côté 2)',   'Tulle (Côté 2)'
    ];

    // Group rows by DPG and create one sheet per group
    const byDPG = {};
    rows.forEach(r => {
      const d = r['DPG (Côté 1)'] || 'No DPG';
      (byDPG[d] ||= []).push(r);
    });

    for (const [dpgValue, groupRows] of Object.entries(byDPG)) {
      const ws = wb.addWorksheet(`DPG ${dpgValue}`, { views: [{ showGridLines: false }] });

      // Column widths & header heights
      const widths        = [25,15,17,14,13,13,21,21,15,21,13,13,21,21,14,17,17];
      const headerHeights = [24,34,22,24];
      widths.forEach((w, i) => (ws.getColumn(i + 1).width = w));
      headerHeights.forEach((h, r) => (ws.getRow(r + 1).height = h));
      ws.getRow(2).height = 120;

      /* Row 1: labels */
      ws.mergeCells('A1:G1'); Object.assign(ws.getCell('A1'), { value: 'Module',   font: headerFont, alignment: centerAll });
      ws.mergeCells('H1:I1'); Object.assign(ws.getCell('H1'), { value: 'Stand',    font: headerFont, alignment: centerAll });
      ws.mergeCells('J1:L1'); Object.assign(ws.getCell('J1'), { value: 'QS',       font: headerFont, alignment: centerAll });
      ws.mergeCells('M1:Q1'); Object.assign(ws.getCell('M1'), { value: 'Remarque', font: headerFont, alignment: centerAll });
      fillRange(ws, 1, 1, 1, 17, GREY);
      borderBottom(ws, 1, 1, 17, 'medium');
      verticalDivider(ws, 7, 1, 1); verticalDivider(ws, 9, 1, 1); verticalDivider(ws, 12, 1, 1);
      edgeLine(ws, 1, 1, 1, 'left'); edgeLine(ws, 17, 1, 1, 'right');

      /* Row 2: values */
      ws.mergeCells('A2:G2'); Object.assign(ws.getCell('A2'), { value: header.module ?? '', alignment: centerAll });
      ws.mergeCells('H2:I2'); Object.assign(ws.getCell('H2'), { value: header.stand  ?? '', alignment: centerAll });
      ws.mergeCells('J2:L2'); Object.assign(ws.getCell('J2'), { value: header.qs     ?? '', alignment: centerAll });
      ws.mergeCells('M2:Q2'); Object.assign(ws.getCell('M2'), {
        value: (header.remarque || '').replace(/,\s*/g, '\n'), // vertical list
        alignment: { horizontal: 'left', vertical: 'top', wrapText: true },
        font: { name: 'Calibri', size: 12, color: { argb: 'FFFF0000' } }
      });
      ws.getCell('A2').font = { name: 'Calibri', size: 22, bold: true };
      ws.getCell('H2').font = { name: 'Calibri', size: 22, bold: true };
      verticalDivider(ws, 7, 2, 2); verticalDivider(ws, 9, 2, 2); verticalDivider(ws, 12, 2, 2);
      edgeLine(ws, 1, 2, 2, 'left'); edgeLine(ws, 17, 2, 2, 'right');
      borderBottom(ws, 2, 1, 17, 'medium');

      /* Row 3: group headers */
      ws.mergeCells('A3:F3'); Object.assign(ws.getCell('A3'), { value: 'Côté 1', font: headerFont, alignment: centerAll });
      ws.mergeCells('K3:L3'); Object.assign(ws.getCell('K3'), { value: 'Couleur', font: headerFont, alignment: centerAll });
      ws.mergeCells('M3:Q3'); Object.assign(ws.getCell('M3'), { value: 'Côté 2',  font: headerFont, alignment: centerAll });
      fillRange(ws, 3, 1, 3, 17, GREY); borderBottom(ws, 3, 1, 17, 'medium');
      verticalDivider(ws, 6, 3, 3); verticalDivider(ws, 10, 3, 3); verticalDivider(ws, 12, 3, 3);
      edgeLine(ws, 1, 3, 3, 'left'); edgeLine(ws, 17, 3, 3, 'right');
      ['Câble','Matériel','section','Longueur (mm)'].forEach((txt, i) => {
        Object.assign(ws.getCell(3, 7 + i), { value: txt, font: headerFont, alignment: centerAll });
      });

      /* Row 4: column titles */
      for (let i = 0; i < titles.length; i++) {
        const cc = ws.getRow(4).getCell(i + 1);
        cc.value     = titles[i];
        cc.font      = headerFont;
        cc.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cc.fill      = { type: 'pattern', pattern: 'solid', fgColor: GREY };
        cc.border    = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
      }

      /* ───────────────────────────────────────────────
         NEW: normalize "Matériel (Côté 2)" by Pos Nr. (Côté 2)
         Every identical Pos2 gets the first non-empty Mat2.
         ─────────────────────────────────────────────── */
      const rowsForSheet = groupRows.map(r => ({ ...r }));  // shallow copy
      const pos2ToMat2 = {};
      rowsForSheet.forEach(r => {
        const p = (r['Pos Nr. (Côté 2)'] ?? '').toString().trim();
        const m = (r['Matériel (Côté 2)'] ?? '').toString().trim();
        if (p && m && !pos2ToMat2[p]) pos2ToMat2[p] = m;
      });
      rowsForSheet.forEach(r => {
        const p = (r['Pos Nr. (Côté 2)'] ?? '').toString().trim();
        if (p && pos2ToMat2[p]) r['Matériel (Côté 2)'] = pos2ToMat2[p];
      });

      /* Data rows (start at row 5) */
      rowsForSheet.forEach((rowData, ri) => {
        const r = ws.getRow(5 + ri);
        for (let i = 0; i < titles.length; i++) {
          const t = titles[i];
          const cell = r.getCell(i + 1);
          const val  = rowData[t] ?? '';

          if (t === 'Tulle (Côté 1)') {
            cell.value = '';
          } else if (t === 'Couleur 1' || t === 'Couleur 2') {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: val || 'FFFFFFFF' } };
            cell.value = '';
          } else if (t === 'Longueur (mm)') {
            const n = Number(val);
            if (Number.isFinite(n)) {
              cell.value = n;
              cell.numFmt = '0'; // integer, no trailing dot
            } else {
              cell.value = null;
            }
          } else if (t === 'section') {
            const n = Number(val);
            cell.value = Number.isFinite(n) ? n : null;
            if (Number.isFinite(n)) cell.numFmt = '0.##';
          } else {
            cell.value = val;
          }

          cell.font      = dataFont;
          cell.alignment = { vertical: 'middle', horizontal: 'center' }; // center everything
          cell.border    = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
        }
      });

      const lastDataRow = 4 + rowsForSheet.length;

      // Merge columns with repeated values (contiguous runs)
      mergeColumn(ws, 1,  5, lastDataRow);   // NEW: Pos Nr. (Côté 1)  (col A)
      mergeColumn(ws, 2,  5, lastDataRow);   // DPG (Côté 1)
      mergeColumn(ws, 9,  5, lastDataRow);   // section
      mergeColumn(ws, 13, 5, lastDataRow);   // Pos Nr. (Côté 2)

      /* “Longueur de dénudage” block — unchanged */
      const start = lastDataRow + 3;
      const end   = start + (sectionRows.length || 0) - 1;
      if (sectionRows.length > 0) {
        ws.mergeCells(`A${start}:A${end}`);
        Object.assign(ws.getCell(`A${start}`), {
          value: 'Longueur de\ndénudage',
          font: headerFont,
          alignment: centerAll,
          border: { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} }
        });
        sectionRows.forEach((s, i) => {
          const R = start + i;
          Object.assign(ws.getCell(`B${R}`), { value: s.code || '',   font: dataFont, alignment: centerAll });
          ws.mergeCells(`C${R}:D${R}`);
          Object.assign(ws.getCell(`C${R}`), { value: s.length || '', font: dataFont, alignment: centerAll });
          ['B','C','D'].forEach(col => {
            const cell = ws.getCell(`${col}${R}`);
            cell.border = { top:{style:'thin'}, left:{style:'thin'}, bottom:{style:'thin'}, right:{style:'thin'} };
          });
        });
      }
    }

    // Use module value as filename if available
    const base = (header?.module || 'final') + '_' + Date.now();
    const buf = await wb.xlsx.writeBuffer();
    saveAs(new Blob([buf]), `${base}.xlsx`);
  };

  return (
    <button onClick={download} disabled={rows.length === 0} style={{ marginTop: 20 }}>
      Export Excel
    </button>
  );
}
